import { useCallback, useState } from 'react';

import { CAN_VIEW_SECURITY } from '../../../../constants';
import { FeatureSelection, FeatureName } from '../types';

type UseFeatureSelectionParams = {
  initialValues: FeatureSelection;
};

type Result = {
  featureSelection: FeatureSelection;
  updateFeature(feature: FeatureName, selected: boolean): void;
  resetFeatures(values: FeatureSelection): void;
};

export const getKentFeatureSelection = (): FeatureSelection => ({
  costMonitoring: true,
  kent: true,
  autoscaler: true,
  security: false,
  workloadAutoscaler: false,
  omni: false,
  nvidiaGpuPlugin: false,
  networkCostMetrics: false,
  gpuMetricsExporter: false,
  reliabilityMetrics: false,
});

export const getDefaultFeatureSelection = ({
  isSecuritySunsetEnabled = false,
  isTrialExpired = false,
  reportingMonetizationApplies = false,
}: {
  isSecuritySunsetEnabled?: boolean;
  isTrialExpired?: boolean;
  reportingMonetizationApplies?: boolean;
} = {}): FeatureSelection => {
  const skipPremiumDefaults = isTrialExpired || reportingMonetizationApplies;

  return {
    costMonitoring: true,
    autoscaler: !skipPremiumDefaults,
    security: isSecuritySunsetEnabled,
    workloadAutoscaler: !skipPremiumDefaults,
    omni: false,
    nvidiaGpuPlugin: false,
    networkCostMetrics: false,
    gpuMetricsExporter: false,
    reliabilityMetrics: false,
    kent: false,
  };
};

export const buildValuesForFeaturePromotion = (
  promotedFeature: FeatureName,
  { isSecuritySunsetEnabled = false, isTrialExpired = false } = {}
): FeatureSelection => ({
  costMonitoring: true,
  autoscaler: !isTrialExpired && promotedFeature === 'autoscaler',
  security:
    isSecuritySunsetEnabled ||
    (promotedFeature === 'security' && CAN_VIEW_SECURITY()),
  workloadAutoscaler:
    !isTrialExpired && promotedFeature === 'workloadAutoscaler',
  omni: false,
  nvidiaGpuPlugin: false,
  networkCostMetrics: promotedFeature === 'networkCostMetrics',
  gpuMetricsExporter: promotedFeature === 'gpuMetricsExporter',
  reliabilityMetrics: false,
  kent: false,
});

export const COST_MONITORING_ONLY: FeatureSelection = {
  costMonitoring: true,
  autoscaler: false,
  security: false,
  workloadAutoscaler: false,
  omni: false,
  nvidiaGpuPlugin: false,
  networkCostMetrics: false,
  gpuMetricsExporter: false,
  reliabilityMetrics: false,
  kent: false,
};

export const useFeatureSelection = ({
  initialValues,
}: UseFeatureSelectionParams): Result => {
  const [featureSelection, setFeatureSelection] =
    useState<FeatureSelection>(initialValues);

  const updateFeature = useCallback(
    (feature: FeatureName, selected: boolean) => {
      setFeatureSelection((prev) => {
        const next = { ...prev, [feature]: selected };

        // Reset autoscaler dependent components if autoscaler is deselected
        if (feature === 'autoscaler' && !selected) {
          next.nvidiaGpuPlugin = false;
          next.omni = false;
        }

        return next;
      });
    },
    []
  );

  const resetFeatures = useCallback((values: FeatureSelection) => {
    setFeatureSelection(values);
  }, []);

  return { featureSelection, updateFeature, resetFeatures };
};
